import { FormattedMessage, useIntl } from 'react-intl'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'

function SortOrderInfoModal() {
  const intl = useIntl()

  const title = intl.formatMessage({
    id: 's4PRlTOa3fK5q9qrhXrX',
    defaultMessage: 'How our hotel search results work',
  })

  const introText = intl.formatMessage({
    id: 'F4l9241xUG48j0AGnsPa',
    defaultMessage: 'Our goal is to show you the most relevant and valuable hotel offers as quickly as possible. This page explains how we decide which hotels to show, the order we show them in, and how paid placements work.',
  })

  const recommendedHeading = intl.formatMessage({
    id: 'izi8SPkdPbxoCvlR8ceF',
    defaultMessage: 'Default sort order: "Recommended"',
  })

  const recommendedBody = intl.formatMessage({
    id: 'ZGRguT5JxyrNtwJ9W4e6',
    defaultMessage: 'When you search for hotels, results are displayed in our Recommended order by default. This is designed to surface the offers most likely to match what you are looking for. Location and relevance to your search are the primary factors we consider.',
  })

  const factorsIntro = intl.formatMessage({
    id: 'G6qr23QbD6hnPpp-8Vaf',
    defaultMessage: 'The main factors we consider are:',
  })

  const otherSortHeading = intl.formatMessage({
    id: 'JDotg_goJm4iBaDdbs73',
    defaultMessage: 'Other sort options',
  })

  const paidPlacementsHeading = intl.formatMessage({
    id: 'ztOPuaGVpK9_qMEsOza7',
    defaultMessage: 'Paid placements',
  })

  const paidPlacementsBody = intl.formatMessage({
    id: 'qRN6edfbZC07u9Frib91',
    defaultMessage: 'Some hotel offers appear in a fixed position in our search results because the supplier has paid for that placement. All paid placements are clearly labelled with an "Ad" badge so you can distinguish them from organically ranked results at a glance. The "Ad" label appears on each individual paid placement, in the position where that placement displays. Paid placement affects position only. It does not influence how the listing appears in any other way, including image size, badges, or any labelling.',
  })

  const personalisationHeading = intl.formatMessage({
    id: 'noOq0oa7tz_uzIEOs3Gq',
    defaultMessage: 'Personalisation and testing',
  })

  const personalisationBody = intl.formatMessage({
    id: 'RRXXxY3cqDacz_OPdG8p',
    defaultMessage: 'The default Recommended sort order is consistent across web and app for the same user and the same search. Luxury Escapes retains the right to run limited tests that may temporarily affect how listings and search results are displayed, and to display results differently across markets where relevant. Paid placements remain clearly labelled during any such tests.',
  })

  const aiContentHeading = intl.formatMessage({
    id: 'T3FB_9QDnEQc6JhtdZMH',
    defaultMessage: 'AI-generated content in search results',
  })

  const aiContentBody = intl.formatMessage({
    id: 'IEriCRcpuhdWSPefrJmW',
    defaultMessage: 'When you search using a destination name or a descriptive term, some results may include a short AI-generated explanation of why particular offers are considered strong matches for your search. These explanations are generated automatically from the offer details and your search. They do not use commercial factors, do not affect a property’s ranking position, and do not appear on paid placements. We encourage you to review the full property details before making a booking decision.',
  })

  return (
    <Modal title={title} size="L">
      <VerticalSpacer gap={24}>
        <BodyText variant="medium">{introText}</BodyText>

        <VerticalSpacer gap={12}>
          <BodyTextBlock variant="medium" weight="bold">
            {recommendedHeading}
          </BodyTextBlock>
          <BodyText variant="medium">{recommendedBody}</BodyText>
          <BodyText variant="medium">{factorsIntro}</BodyText>
          <Group as="ul" direction="vertical" gap={8} horizontalAlign="start">
            <BodyText variant="medium" as="li">
              <FormattedMessage
                id="SdmanYmugd0k-qAyUHzV"
                defaultMessage="<b>What we earn.</b> We also consider how much Luxury Escapes earns when you book, based on the margin we receive per completed booking and how often an offer is booked. Where offers are otherwise comparable, we may rank a property that generates a stronger commercial return for Luxury Escapes above one that does not."
                
                values={{ b: (chunks: Array<React.ReactNode>) => <b>{chunks}</b> }}
              />
            </BodyText>
            <BodyText variant="medium" as="li">
              <FormattedMessage
                id="ybC2sGUc4qZvlA1uUbXv"
                defaultMessage="<b>Location.</b> We consider the property's location relative to your search — for example, proximity to the destination or area you searched for."
                
                values={{ b: (chunks: Array<React.ReactNode>) => <b>{chunks}</b> }}
              />
            </BodyText>
            <BodyText variant="medium" as="li">
              <FormattedMessage
                id="a4MVA0P_5wBcST7ht3Vz"
                defaultMessage="<b>Loyalty status.</b> Where applicable, your Luxury Escapes membership status may influence how results are ordered for you."
                
                values={{ b: (chunks: Array<React.ReactNode>) => <b>{chunks}</b> }}
              />
            </BodyText>
            <BodyText variant="medium" as="li">
              <FormattedMessage
                id="U7u_PueKewSn7RPpG9zh"
                defaultMessage="<b>New listings.</b> We may temporarily rank newly listed properties higher in search results to give them initial visibility and ensure our platform reflects the full breadth of our offer."
                
                values={{ b: (chunks: Array<React.ReactNode>) => <b>{chunks}</b> }}
              />
            </BodyText>
            <BodyText variant="medium" as="li">
              <FormattedMessage
                id="UFT6yUm8kS-ktjC0_Zc7"
                defaultMessage="<b>Tiebreaking.</b> Where two or more offers are equal on the factors above, we use other engagement signals. Where offers remain equal after that, order is determined randomly."
                
                values={{ b: (chunks: Array<React.ReactNode>) => <b>{chunks}</b> }}
              />
            </BodyText>
          </Group>
        </VerticalSpacer>

        <VerticalSpacer gap={8}>
          <BodyTextBlock variant="medium" weight="bold">
            {otherSortHeading}
          </BodyTextBlock>
          <BodyText variant="medium">
            <FormattedMessage
              id="xmwHs32W1hrgCnSMBuZc"
              // "Best Value" criteria wording is an unconfirmed placeholder pending product sign-off.
              defaultMessage="You can change the default sort at any time using the options available on the search results page. Options include Price (Lowest), Price (Highest), Best Value, and Customer Rating (Highest). Best Value ranks offers using a combination of price and customer rating. Commercial factors do not influence any non-default sort."
              
            />
          </BodyText>
        </VerticalSpacer>

        <VerticalSpacer gap={8}>
          <BodyTextBlock variant="medium" weight="bold">{paidPlacementsHeading}</BodyTextBlock>
          <BodyText variant="medium">{paidPlacementsBody}</BodyText>
        </VerticalSpacer>

        <VerticalSpacer gap={8}>
          <BodyTextBlock variant="medium" weight="bold">
            {personalisationHeading}
          </BodyTextBlock>
          <BodyText variant="medium">{personalisationBody}</BodyText>
        </VerticalSpacer>

        <VerticalSpacer gap={8}>
          <BodyTextBlock variant="medium" weight="bold">
            {aiContentHeading}
          </BodyTextBlock>
          <BodyText variant="medium">{aiContentBody}</BodyText>
        </VerticalSpacer>
      </VerticalSpacer>
    </Modal>
  )
}

export default SortOrderInfoModal
